import Link from '@pinecast/common/Link';
import Select from '@pinecast/common/Select';
import type {IntlShape} from '@pinecast/i18n';
import {FormattedMessage, useIntl} from '@pinecast/i18n';
import {useCSS} from '@pinecast/styles';
import * as React from 'react';

import {NARROW} from './layout';

type Props = {
  index: number;
  /** The `lang` of a speaker's name in read mode, for a page in another
   * language. */
  language?: string | null;
  speaker: string;
  speakerMap?: Record<string, string>;
  allSpeakerLabels: Array<string>;
  showSpeakerLabels: boolean;
  onEdit?: (speaker: string, index: number) => Promise<void>;
  canMergeWithPrevious?: boolean;
  onMerge?: () => Promise<void>;
};

function formatSpeaker(
  letter: string,
  speakerMap: Record<string, string> | undefined,
  showSpeakerLabels: boolean,
  intl: IntlShape,
  addSuffix: string = ':',
) {
  const character = speakerMap?.[letter];
  if (character) {
    if (showSpeakerLabels && character !== letter) {
      return intl.formatMessage(
        {
          description:
            'Label for the speaker in the transcript when speakers are annotated with names. {letter} is the letter of the alphabet corresponding to the speaker. {name} is the name of the speaker. {suffix} is an optional colon or other punctuation.',
          defaultMessage: '{name} ({letter}){suffix}',
        },
        {
          letter,
          name: character,
          suffix: addSuffix,
        },
      );
    }
    return character;
  } else if (letter.length > 1) {
    return letter;
  }
  return intl.formatMessage(
    {
      description:
        'Default label for the speaker in the transcript when speakers are not annotated with names. {letter} is the letter of the alphabet corresponding to the speaker. {suffix} is an optional colon or other punctuation.',
      defaultMessage: 'Speaker {letter}{suffix}',
    },
    {letter, suffix: addSuffix},
  );
}

function unusedLetter(usedLetters: Array<string>) {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  for (const letter of letters) {
    if (!usedLetters.includes(letter)) {
      return letter;
    }
  }
  return null;
}

const ChangePendingSelect = ({
  label,
  value,
  onChange,
  speakerMap,
  allSpeakerLabels,
}: {
  label: string;
  value: string;
  onChange: (value: string) => Promise<void>;
  speakerMap?: Record<string, string>;
  allSpeakerLabels: Array<string>;
}) => {
  const intl = useIntl();
  const [pending, setPending] = React.useState(false);

  const newLetter = unusedLetter(allSpeakerLabels);
  const labels = React.useMemo(
    () =>
      allSpeakerLabels.sort().map(letter => ({
        key: letter,
        label: formatSpeaker(letter, speakerMap, true, intl, ''),
      })),
    [allSpeakerLabels, speakerMap, intl],
  );
  return (
    <Select
      aria-label={label}
      disabled={pending}
      value={value}
      onChange={newSpeaker => {
        setPending(true);
        onChange(newSpeaker).finally(() => setPending(false));
      }}
      options={
        newLetter
          ? labels.concat([
              {
                key: newLetter,
                label: intl.formatMessage(
                  {
                    description:
                      'Label for the new speaker option in the transcript when speakers are annotated with names. {letter} is the letter of the alphabet corresponding to the speaker.',
                    defaultMessage: 'New speaker ({letter})',
                  },
                  {
                    letter: newLetter,
                  },
                ),
              },
            ])
          : labels
      }
    />
  );
};

const MergeLink = ({onMerge}: {onMerge: () => Promise<void>}) => {
  const [pending, setPending] = React.useState(false);
  const handleClick = React.useCallback(() => {
    setPending(true);
    onMerge().finally(() => setPending(false));
  }, [onMerge]);
  return (
    <Link onClick={handleClick} disabled={pending}>
      <FormattedMessage
        description="Link text to merge this utterance with the one above it"
        defaultMessage="Merge with above"
      />
    </Link>
  );
};

const TranscriptUtteranceSpeakerEditor = ({
  language,
  speaker,
  speakerMap,
  allSpeakerLabels,
  showSpeakerLabels,
  index,
  onEdit,
  canMergeWithPrevious,
  onMerge,
}: Props) => {
  const css = useCSS();
  const intl = useIntl();
  const handleChange = React.useCallback(
    (value: string) => {
      if (value !== speaker && onEdit) {
        return onEdit(value, index);
      }
      return Promise.resolve();
    },
    [speaker, index, onEdit],
  );
  return onEdit ? (
    <div
      className={css({display: 'flex', flexDirection: 'column', gap: '4px'})}
    >
      <ChangePendingSelect
        label={intl.formatMessage(
          {
            description:
              'Accessibility label for the menu that picks the speaker of one line of a transcript in the transcript editor. {line} is the number of the line, counting from 1.',
            defaultMessage: 'Line {line, number} speaker',
          },
          {line: index + 1},
        )}
        value={speaker}
        onChange={handleChange}
        allSpeakerLabels={allSpeakerLabels}
        speakerMap={speakerMap}
      />
      {canMergeWithPrevious && onMerge && <MergeLink onMerge={onMerge} />}
    </div>
  ) : (
    // A <b>, as HTML marks up the speakers in a conversation. It takes the
    // page's bold face: Monument Grotesk Bold on Pinecast's own pages, and
    // the site's font on a podcast website, which has no Monument Grotesk.
    <b
      className={css({
        // A long name wraps in its column instead of pushing the transcript
        // off a narrow screen (WCAG 1.4.10), even a name with no spaces.
        overflowWrap: 'anywhere',
        textAlign: 'right',
        [NARROW]: {
          // On its own line, just above the words. The space above the name
          // separates the turns.
          marginBottom: '2px',
          marginTop: '12px',
          textAlign: 'start',
          ':first-child': {marginTop: 0},
        },
      })}
      // A name from the show is in the show's language. "Speaker A" is ours,
      // in the page's language.
      {...(speakerMap?.[speaker]
        ? {dir: 'auto', lang: language ?? undefined}
        : {})}
    >
      {formatSpeaker(speaker, speakerMap, !!showSpeakerLabels, intl)}
    </b>
  );
};

export default TranscriptUtteranceSpeakerEditor;
